import React, { useState, useEffect, useRef } from "react"
import Masonry from "react-masonry-css"
import cx from "classnames"
import Sticky from "react-sticky-el"
import debounce from "lodash/debounce"
import useSmartCrop from "@/hooks/useSmartCrop"

import OrderProgressiveLoading from "@/components/Shared/OrderHistory/v1/OrderProgressiveLoading"
import PageLoader from "@/components/core/PageLoader/PageLoader"
import Modal from "@/components/core/Modal/Modal"
import AccountsLayout from "@/components/AccountsLayout/AccountsLayout"
import Sort from "@/components/ProductList/v2/Sort"
import Button from "@/components/core/Button/Button"
import ProductsPerPage from "@/components/ProductList/v2/ProductListView/ProductsPerPage"
import FavoritesTile from "@/components/MyFavorites/v1/FavoritesTile"
import FavoriteNoResult from "@/components/MyFavorites/v1/FavoriteNoResult"

import { replaceSearchUrl, getSwatchCount } from "@/utils/helper"
import MyLists from "@/components/MyFavorites/v1/MyList"
import editIcon from "@/public/icons/edit.svg"
import backToTop from "@/public/icons/back-top-arrow.svg"
import closeIcon from "@/public/icons/close-favorite.svg"
import { dataLayerlistTab } from "@/components/MyFavorites/v1/MyList/analytics"

const FavoritesView = props => {
  const {
    tabList = [],
    staticTexts = {},
    handleDelete = () => {},
    sorts = [],
    editMode = false,
    handleEditMode = () => {},
    handleSelect = () => {},
    continueShopUrl = "",
    selectedItems = [],
    presetConfigs = "",
    isAuth = false,
    user = {},
    selectedCount = 0,
    isFavoritesLoading = false,
    logout = () => {},
    redirect = () => {},
    handleCheckAll = () => {},
    authoredContent = {},
    handleSelectAll = () => {},
    selectedAll = false,
    isSelected = false,
    locale = "",
    productsPerPage = [],
    data = [],
    selectedSortItem = {},
    brandSwitcherItems,
    isSaveProductToList = false,
    siteName,
    ...restProps
  } = props || {}
  const { title } = authoredContent
  const {
    itemRemove = "",
    itemCancel = "",
    favoritesDeleteMssg = "",
    favoriteDeleteTitle = "",
    backToTop: staticTextsBackToTop = "",
    loadMore = "",
    ariaLabel: {
      selectedAll: staticTextsSelectedAll = "",
      close = "",
      remove: ariaLabelRemove = "",
      manageSelected: ariaLabelManageSelected = "",
      allTab = "",
      tabs = "",
    },
    ariaManageText = "",
    manageText = "",
    manageFavorites = "",
    UnselectAll = "",
    selectAll = "",
    remove = "",
  } = staticTexts
  const activeTabRef = useRef(null)
  const smartCrop = useSmartCrop()
  const [activeTab, setActiveTab] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [rows, setRows] = useState(30)
  const [tabData, setTabData] = useState(
    () => tabList[activeTab]?.data?.slice(0, rows) ?? []
  )
  const [swatchCount, setSwatchCount] = useState(5)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedSort, setSelectedSort] = useState(selectedSortItem)

  const onResizeHandler = debounce(() => setSwatchCount(getSwatchCount()), 200)
  const [openCreateNewList, setOpenCreateNewList] = useState(false)
  const [isListDetailsPage, setIsListDetailsPage] = useState(false)

  const createNewlist = () => {
    setOpenCreateNewList(true)
  }

  useEffect(() => {
    window.addEventListener("resize", onResizeHandler)
    return () => {
      window.removeEventListener("resize", onResizeHandler)
    }
  }, [])
  const openModal = () => {
    setShowModal(true)
  }
  const closeModal = () => {
    setShowModal(false)
  }

  const enableRemove = selectedCount >= 1
  const manageSelected = useRef(null)

  useEffect(() => {
    const currentTabData = tabList[activeTab]?.data
    if (currentTabData !== undefined) {
      setTabData(currentTabData?.slice(0, rows))
    } else {
      setActiveTab(0)
      setTabData(tabList[0]?.data)
    }
  }, [tabList, activeTab, rows])

  const handleManageTool = () => {
    handleEditMode(!editMode)
    manageSelected?.current?.focus()
    handleCheckAll()
  }

  const changeTab = tab => {
    const myListTitle = staticTexts?.myLists
    const tabName = tabList[tab]?.title
    setActiveTab(tab)
    if (activeTab !== tab) {
      handleCheckAll()
    }
    dataLayerlistTab(myListTitle, tabName)
  }
  useEffect(() => {
    if (data) {
      handleSort(selectedSort)
    }
  }, [data, activeTab])

  const handleSort = sortType => {
    const activeTabData = tabList[activeTab]?.data || []
    if (sortType?.query === "old") {
      setTabData(
        [...activeTabData]?.sort(
          (a, b) => new Date(a.addedAt) - new Date(b.addedAt)
        )
      )
    } else {
      setTabData(
        [...activeTabData]?.sort(
          (a, b) => new Date(b.addedAt) - new Date(a.addedAt)
        )
      )
    }
    setSelectedSort(sortType)
    replaceSearchUrl("sort", sortType?.query)

    setIsOpen(false)
  }

  const getSelectedProductsAnalyticName = (
    selectedProducts,
    isSelectDeSelectAll = false
  ) => {
    let data = []
    if (selectedProducts && selectedProducts?.length) {
      const selectedProductItems = isSelectDeSelectAll
        ? [...tabData]
        : tabData?.filter(obj =>
            selectedProducts?.some(item => obj?.id === (item?.id || item))
          )
      data = selectedProductItems?.map(i =>
        i?.productData?.productName_s?.toLowerCase()
      )
    }

    return data?.length ? data?.join("|") : "n/a"
  }

  const handleKeyPress = e => {
    if (e.keyCode === 37 || e.keyCode === 39) {
      let tab
      if (e.keyCode === 37) {
        tab = activeTab - 1
      } else if (e.keyCode === 39) {
        tab = activeTab + 1
      }
      if (tab < 0) tab = tabList.length - 1
      if (tab >= tabList.length) tab = 0
      document.getElementById(`${tabList[tab].title}-tab`).focus()
    }
  }

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    activeTabRef?.current?.childNodes[activeTab]?.focus()
  }

  const handleRows = row => {
    setRows(row)
    setTabData(tabList[activeTab]?.data?.slice(0, row))
  }

  const handleLoadMore = () => {
    setTabData(
      tabList[activeTab]?.data?.slice(
        0,
        tabList[activeTab]?.data?.length + rows
      )
    )
  }

  const getEventInfo = type => {
    const selectedTab = tabList[activeTab]
      ? tabList[activeTab]?.title?.toLowerCase()
      : ""
    return JSON.stringify({
      clickInternalLinks: "true",
      eventAction: `my account:favorites:${selectedTab}:${
        type ? type + ":" : ""
      }close`,
      eventName: `my account:favorites:${selectedTab}:${
        type ? type + ":" : ""
      }close`,
      eventType: "navigation",
      eventMsg: "n/a",
      eventStatus: "n/a",
      internalLinkName: "close",
      internalLinkPosition: "favorites",
      internalLinkType: "my account:close",
      internalLinkURL: "n/a",
      internalLinkZoneName: `my account:favorites:${selectedTab}${
        type ? ":" + type : ""
      }`,
    })
  }

  const getSelectUnSelectEventInfo = (isSelected, tabData) => {
    const selectUnSelect = isSelected ? "select all" : "unselect all"
    const selectedTab = tabList[activeTab]
      ? tabList[activeTab]?.title?.toLowerCase()
      : ""

    const selectedProducts = getSelectedProductsAnalyticName(tabData, true)

    const eventInfo = {
      clickInternalLinks: "true",
      eventAction: `my account:favorites:${selectedTab}:${selectUnSelect}`,
      eventName: `my account:favorites:${selectedTab}:${selectUnSelect}`,
      eventType: "navigation",
      eventMsg: "n/a",
      eventStatus: "n/a",
      internalLinkName: selectUnSelect,
      internalLinkPosition: "favorites",
      internalLinkType: "my account:" + selectedProducts,
      internalLinkURL: "n/a",
      internalLinkZoneName: `my account:favorites:${selectedTab}`,
    }

    return JSON.stringify(eventInfo)
  }

  const addRemoveAnalyticsData = (type, removeOrCancel) => {
    const removeOrCancelType = removeOrCancel || "remove"
    const { adobeDataLayer: { getState } = {} } = window
    const page = (getState && getState("page")) || {}
    const selectedTab = tabList[activeTab]
      ? tabList[activeTab]?.title?.toLowerCase()
      : ""
    const eventInfo = {
      eventAction: `my account:favorites:${selectedTab}:${
        type ? type + ":" : ""
      }${removeOrCancelType}`,
      eventName: `my account:favorites:${selectedTab}:${
        type ? type + ":" : ""
      }${removeOrCancelType}`,
      eventType: "navigation",
      eventMsg: "n/a",
      eventStatus: "n/a",
      internalLinkName: removeOrCancelType,
      internalLinkPosition: "favorites",
      internalLinkType: `my account:${getSelectedProductsAnalyticName(
        selectedItems
      )}`,
      internalLinkZoneName: `my account:favorites:${selectedTab}${
        type ? ":" + type : ""
      }`,
      internalLinkURL: "n/a",
      clickInternalLinks: "true",
    }

    window.adobeDataLayer.push({
      event: "cmp:click",
      eventInfo,
      page,
    })
  }

  const removeClickHandler = () => {
    addRemoveAnalyticsData()
    if (enableRemove) openModal()
    else return
  }

  const itemRemoveClickHandler = () => {
    addRemoveAnalyticsData("remove item modal", "yes remove item")
    handleDelete(closeModal)
  }

  const cancelRemoveItemClickHandler = () => {
    addRemoveAnalyticsData("remove item modal", "no keep item")
    closeModal()
  }
  if (isFavoritesLoading === "loading") {
    return (
      <AccountsLayout
        title={
          isSaveProductToList
            ? staticTexts?.myLists
            : staticTexts?.favoriteTitle
        }
        user={user}
        activeTab={0}
        i18n={staticTexts}
        data={data}
        redirect={redirect}
        logout={logout}
      >
        <PageLoader loading={true} />
        <OrderProgressiveLoading />
      </AccountsLayout>
    )
  }
  return (
    <>
      <div className="favorites-container">
        <AccountsLayout
          isListDetailsPage={isListDetailsPage}
          activeTab={1}
          i18n={staticTexts}
          isAuth={isAuth}
          user={user}
          logout={logout}
          data={data}
          redirect={redirect}
          isSaveProductToList={isSaveProductToList}
          createNewBtnText={staticTexts?.createNewList}
          createNewlist={createNewlist}
        >
          <div className="favorites">
            {isFavoritesLoading === "loading" ? (
              <Loader />
            ) : (
              <>
                {tabList && tabList.length === 0 ? (
                  <FavoriteNoResult
                    continueShopUrl={continueShopUrl}
                    authoredContent={authoredContent}
                    staticTexts={staticTexts}
                    {...restProps}
                  />
                ) : isSaveProductToList && activeTab === 0 ? (
                  <MyLists
                    siteName={siteName}
                    tabList={tabList}
                    presetConfigs={presetConfigs}
                    sorts={sorts}
                    tabChange={changeTab}
                    openCreateNewList={openCreateNewList}
                    createNewListCallBAck={setOpenCreateNewList}
                    query={props?.query}
                    setIsListDetailsPage={setIsListDetailsPage}
                  />
                ) : (
                  <React.Fragment>
                    <div className="favorites__header">
                      <div className="container favorites__container">
                        <h1 className="favorites__title">
                          {isSaveProductToList ? staticTexts?.myLists : title}
                        </h1>
                      </div>
                      <div className="favorites__tab-header">
                        <div
                          id="fav-tab-panel"
                          className="container favorites__tab-buttons"
                          role="tablist"
                          ref={activeTabRef}
                        >
                          {tabList?.map((tab, i) => (
                            <button
                              key={i}
                              id={`${tab?.title}-tab`}
                              aria-label={
                                tab?.title !== "All"
                                  ? tabs(tab?.data?.length, tab?.title)
                                  : allTab
                              }
                              aria-selected={activeTab === i ? true : false}
                              role="tab"
                              data-gbh-data-layer={`{"eventAction":"my account:favorites:hero:menu","eventName":"my account:favorites:hero:menu","eventType":"navigation","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"${tab.title}","internalLinkPosition":"favorites","internalLinkType":"my account:navigation","internalLinkZoneName":"my account:favorites:hero","internalLinkURL":"n/a","clickInternalLinks":"true"}`}
                              className={`favorites__tab-button gbh-data-layer ${
                                activeTab === i ? "--active" : ""
                              }`}
                              onClick={() => changeTab(i)}
                              onFocus={() => changeTab(i)}
                              onKeyDown={e => handleKeyPress(e)}
                              tabIndex={
                                tabList[activeTab]?.title === tab?.title
                                  ? 0
                                  : -1
                              }
                            >
                              <span className="tab-menu">{tab?.title}</span>
                              {tab?.data?.length > 0 && !isSaveProductToList ? (
                                <span className="tab-item-count">
                                  {tab?.title !== "All"
                                    ? `(${tab?.data?.length})`
                                    : ""}
                                </span>
                              ) : null}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="container favorites__container">
                      <div className="row">
                        <div className="col">
                          <div
                            className={`favorites__favorite-controls ${
                              editMode ? "show-nav" : ""
                            }`}
                          >
                            <button
                              id="kf-fav-manage-btn"
                              role="button"
                              aria-label={manageText + " " + ariaManageText}
                              className="favorites__manage-tools gbh-data-layer"
                              data-gbh-data-layer={`{"eventAction":"my account:favorites:${
                                tabList[activeTab]
                                  ? tabList[activeTab]?.title
                                  : "n/a"
                              }:manage","eventName":"my account:favorites:${
                                tabList[activeTab]
                                  ? tabList[activeTab]?.title
                                  : "n/a"
                              }:manage","eventType":"navigation","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"manage","internalLinkPosition":"favorites","internalLinkType":"my account:navigation","internalLinkZoneName":"my account:favorites:${
                                tabList[activeTab]
                                  ? tabList[activeTab]?.title
                                  : "n/a"
                              }","internalLinkURL":"n/a","clickInternalLinks":"true"}`}
                              onClick={handleManageTool}
                              tabIndex={!editMode ? "0" : "-1"}
                            >
                              <img
                                src={editIcon?.src}
                                className="manage-tools-icon"
                                alt="Manage tools"
                              />
                              {manageText}
                            </button>
                            <Sticky
                              boundaryElement=".favorites__container"
                              className={`favorites__sticky-nav-wrap ${
                                editMode ? "show-nav-wrap" : ""
                              }`}
                            >
                              <div
                                className={`container-fluid favorites__sticky-nav`}
                                group={manageFavorites}
                                aria-hidden={!editMode}
                              >
                                <div className="row">
                                  <div className="col manage-strip-controls">
                                    <div
                                      className="manage-selected-items"
                                      ref={manageSelected}
                                      tabIndex="-1"
                                      aria-label={ariaLabelManageSelected(
                                        selectedCount
                                      )}
                                    >
                                      <span>{manageFavorites}</span>
                                      <span className="selected-count">{` (${selectedCount})`}</span>
                                    </div>
                                    <div className="favorites__controls">
                                      <span
                                        role="button"
                                        tabIndex={editMode ? "0" : "-1"}
                                        aria-label={
                                          selectedAll ? UnselectAll : selectAll
                                        }
                                        className="manage-menu-item gbh-data-layer"
                                        data-gbh-data-layer={getSelectUnSelectEventInfo(
                                          selectedAll,
                                          tabData
                                        )}
                                        onClick={() =>
                                          handleSelectAll(
                                            tabData?.map(i => i?.id)
                                          )
                                        }
                                      >
                                        {selectedAll ? UnselectAll : selectAll}
                                      </span>

                                      <span
                                        aria-disabled={!enableRemove}
                                        role="button"
                                        tabIndex={editMode ? "0" : "-1"}
                                        aria-label={ariaLabelRemove}
                                        className={cx(
                                          "manage-menu-item",
                                          !enableRemove &&
                                            "manage-menu-item--disable-remove"
                                        )}
                                        onClick={removeClickHandler}
                                      >
                                        {remove}
                                      </span>
                                      <span
                                        role="button"
                                        className="gbh-data-layer"
                                        data-gbh-data-layer={getEventInfo()}
                                      >
                                        <img
                                          className="favorites__close"
                                          tabIndex={editMode ? "0" : "-1"}
                                          src={closeIcon?.src}
                                          role="presentation"
                                          aria-label={close}
                                          onClick={handleManageTool}
                                          alt=""
                                        />
                                      </span>

                                      <span
                                        role="region"
                                        aria-live="polite"
                                        className="aria-messages"
                                        aria-label={
                                          selectedAll
                                            ? staticTextsSelectedAll
                                            : ""
                                        }
                                      ></span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Sticky>

                            <section>
                              <Sort
                                tabIndex={!editMode ? "0" : "-1"}
                                page={"favorites"}
                                selectedtab={
                                  tabList[activeTab]
                                    ? tabList[activeTab]?.title
                                    : ""
                                }
                                data={sorts}
                                texts={staticTexts}
                                onChange={handleSort}
                                isOpen={isOpen}
                                setIsOpen={setIsOpen}
                                selectedSort={selectedSort}
                                isMyFavoriteTab={true}
                              />
                            </section>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col">
                          <div
                            className="favorites__tab-content"
                            role="tabpanel"
                            id="favorites-tab-panel"
                          >
                            <div
                              className={`images-tab ${tabList[activeTab]?.title}`}
                            >
                              <Masonry
                                breakpointCols={{ default: 3, 1200: 2 }}
                                className="my-masonry-grid"
                                columnClassName="my-masonry-grid_column"
                              >
                                {tabData?.map((item, i) => (
                                  <React.Fragment key={i}>
                                    <FavoritesTile
                                      selectedtab={
                                        tabList[activeTab]
                                          ? tabList[activeTab]?.title
                                          : ""
                                      }
                                      index={i}
                                      fid={item?.id}
                                      data={item}
                                      locale={locale}
                                      editMode={editMode}
                                      onSelect={fid =>
                                        handleSelect(fid, tabData)
                                      }
                                      isSelected={isSelected}
                                      selectedAll={selectedAll}
                                      isAuth={isAuth}
                                      user={user}
                                      staticTexts={staticTexts}
                                      swatchCount={swatchCount}
                                      presetConfigs={presetConfigs}
                                      smartCrop={smartCrop}
                                      brandSwitcherItems={brandSwitcherItems}
                                    />
                                  </React.Fragment>
                                ))}
                              </Masonry>
                            </div>
                          </div>
                        </div>
                      </div>
                      {tabList[activeTab]?.data?.length > tabData?.length && (
                        <div className="row">
                          <button
                            className="favorites__load-more gbh-data-layer"
                            tabIndex="0"
                            onClick={handleLoadMore}
                            data-gbh-data-layer={`{"eventAction":"my account:favorites:${
                              tabList[activeTab] ? tabList[activeTab].title : ""
                            }:load more","eventName":"my account:favorites:${
                              tabList[activeTab] ? tabList[activeTab].title : ""
                            }:load more","eventType":"navigation","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"load more","internalLinkPosition":"favorites","internalLinkType":"my account:load more","internalLinkZoneName":"my account:favorites:${
                              tabList[activeTab]
                                ? tabList[activeTab].title
                                : "n/a"
                            }","internalLinkURL":"n/a","clickInternalLinks":"true"}`}
                          >
                            {loadMore}
                          </button>
                        </div>
                      )}
                      {!props.favPage && (
                        <div className="row">
                          <div className="favorites__back-to-top">
                            <ProductsPerPage
                              pos="bottom"
                              curRows={rows}
                              selectedTab={
                                tabList[activeTab]
                                  ? tabList[activeTab]?.title
                                  : ""
                              }
                              totalResults={tabList[activeTab]?.data?.length}
                              page="favorites"
                              data={productsPerPage}
                              texts={staticTexts}
                              onChange={handleRows}
                            />
                            <button
                              role="link"
                              aria-label={staticTextsBackToTop}
                              className="favorites__go-back gbh-data-layer"
                              data-gbh-data-layer={`{"eventAction":"my account:favorites:${
                                tabList[activeTab]
                                  ? tabList[activeTab].title
                                  : ""
                              }:back to top","eventName":"my account:favorites:${
                                tabList[activeTab]
                                  ? tabList[activeTab].title
                                  : ""
                              }:back to top","eventType":"navigation","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"back to top","internalLinkPosition":"favorites","internalLinkType":"my account:back to top","internalLinkZoneName":"my account:favorites:${
                                tabList[activeTab]
                                  ? tabList[activeTab].title
                                  : "n/a"
                              }","internalLinkURL":"n/a","clickInternalLinks":"true"}`}
                              onClick={handleBackToTop}
                            >
                              {staticTextsBackToTop}{" "}
                              <img
                                src={backToTop?.src}
                                alt={staticTextsBackToTop}
                              />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </>
            )}
          </div>
        </AccountsLayout>
        <div className="favorites-modal">
          {showModal && (
            <Modal
              showModal={showModal}
              onModalClose={() => setShowModal(false)}
              labelledBy="favorites__modal-title"
              describedBy="favorites__modal-desc"
              closeButtonAnalytics={getEventInfo("remove item modal")}
            >
              <div className="favorites__modal">
                <div
                  className="favorites__modal-title"
                  id="favorites__modal-title"
                >
                  {favoriteDeleteTitle}
                </div>
                <div
                  className="favorites__modal-desc"
                  id="favorites__modal-desc"
                >
                  {favoritesDeleteMssg}
                </div>
                <div className="favorites__modal-cta">
                  <Button
                    role="button"
                    className="gbh-data-layer"
                    type="ghost"
                    label={itemCancel}
                    onClick={cancelRemoveItemClickHandler}
                  />
                  <Button
                    role="button"
                    type="black"
                    label={itemRemove}
                    onClick={itemRemoveClickHandler}
                  />
                </div>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </>
  )
}
const Loader = () => (
  <div className="product-spinner">
    <div className="product-spinner__image shimmer"></div>
    <div className="product-spinner__content">
      <div className="product-spinner__title shimmer"></div>
      <div className="product-spinner__desc">
        <div className="shimmer"></div>
        <div className="shimmer"></div>
        <div className="shimmer"></div>
      </div>
    </div>
  </div>
)

export default FavoritesView
